import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useShare from '@/src/Share/components/hooks/useShare';
import useReengagement, {
    ReengagementSendObject,
} from '@/src/Share/components/hooks/useReengagement';
import useWebhookSettings, {
    AuthType,
    WebhookSettings,
} from '@/src/Activations/components/hooks/useWebhookSettings';
import { useEventContext } from '@/src/v2/components/EventContext';
import { dedupeShares } from '@/src/v2/lib/eventStats';
import EventPage from '../../chrome/EventPage';
import {
    ConfirmDialog,
    Crumb,
    Dialog,
    Field,
    SectionCard,
    Select,
    Textarea,
    TextInput,
    Toggle,
} from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { useToast } from '../../components/roster';
import { Segmented, TCard, TEmpty, TSkeleton } from '../../components/table';
import '../../pages-event.css';
import './delivery.css';

/*
 * Delivery — the sharing area's tools: share outcomes, retry unsent,
 * reengagement blast, and the per-event share webhook. Everything that
 * sends confirms first; nothing reads "sent" until the server says so.
 */

const plural = (n: number, word: string) =>
    `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

const createdOf = (s: any) => s.createdAt ?? s.created_at;

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'ok' | 'bad' }) {
    return (
        <div className="tile">
            <p className="tl">{label}</p>
            <div className="tn">{value}</div>
            {sub && <div className={tone ? `ts ${tone}` : 'ts'}>{sub}</div>}
        </div>
    );
}

/* ---------------- shares: stats, recent, retry ---------------- */

function SharesBlock({ toast }: { toast: ReturnType<typeof useToast> }) {
    const navigate = useNavigate();
    const { activationId: id, ensureSessions } = useEventContext();
    const { getSharesForActivation, reprocessUnsentShares } = useShare();

    const [shares, setShares] = useState<any[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [sessionCount, setSessionCount] = useState<number | null>(null);
    const [confirmRetry, setConfirmRetry] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const retryLock = useRef(false);

    const fetchShares = useCallback(
        async () => dedupeShares(await getSharesForActivation(id)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [id],
    );

    const load = useCallback(() => {
        let alive = true;
        setFailed(false);
        setShares(null);
        fetchShares()
            .then((rows) => alive && setShares(rows))
            .catch(() => alive && setFailed(true));
        ensureSessions()
            .then((rows) => alive && setSessionCount(rows.length))
            .catch(() => {});
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, fetchShares]);

    useEffect(() => {
        if (!id) return;
        return load();
    }, [id, load]);

    const stats = useMemo(() => {
        const rows = shares ?? [];
        const sent = rows.filter((s) => s.sent).length;
        const reached = new Set(rows.map((s) => s.destination).filter(Boolean)).size;
        const sessionsShared = new Set(rows.map((s) => s.photoSessionId).filter(Boolean)).size;
        const rate = sessionCount ? Math.round((sessionsShared / sessionCount) * 100) : null;
        const recent = [...rows]
            .sort((a, b) => moment(createdOf(b)).valueOf() - moment(createdOf(a)).valueOf())
            .slice(0, 5);
        return { sent, unsent: rows.length - sent, reached, rate, recent };
    }, [shares, sessionCount]);

    const retry = async () => {
        if (retryLock.current) return;
        retryLock.current = true;
        setRetrying(true);
        const before = stats.unsent;
        try {
            await reprocessUnsentShares(id);
            const fresh = await fetchShares().catch(() => null);
            if (fresh) {
                setShares(fresh);
                const left = fresh.filter((s) => !s.sent).length;
                toast.show(
                    left === 0
                        ? `Retry finished — all ${plural(before, 'share')} now sent.`
                        : `Retry requested — ${plural(left, 'share')} still unsent.`,
                );
            } else {
                toast.show('Retry requested. Reload to see updated counts.');
            }
        } catch {
            toast.show('Couldn’t start the retry — try again.');
        } finally {
            retryLock.current = false;
            setRetrying(false);
        }
    };

    return (
        <div className="dl-stack">
            {failed ? (
                <TCard>
                    <TEmpty message="Couldn’t load shares." actionLabel="Retry" onAction={load} />
                </TCard>
            ) : (
                <div className="ev-tiles">
                    <Tile label="Sent" value={shares ? stats.sent.toLocaleString() : '—'} />
                    <Tile
                        label="Unsent"
                        value={shares ? stats.unsent.toLocaleString() : '—'}
                        sub={shares && stats.unsent > 0 ? 'not delivered yet' : undefined}
                        tone={stats.unsent > 0 ? 'bad' : undefined}
                    />
                    <Tile label="People reached" value={shares ? stats.reached.toLocaleString() : '—'} />
                    <Tile label="Share rate" value={shares && stats.rate != null ? `${stats.rate}%` : '—'} sub="of sessions" />
                </div>
            )}

            {shares !== null && stats.unsent > 0 && (
                <SectionCard title="Unsent shares" note={plural(stats.unsent, 'share')}>
                    <p className="dl-p">
                        These guests asked for their photos but the email or text didn’t go out. Retrying sends
                        them now.
                    </p>
                    <button type="button" className="tb-btn" disabled={retrying} onClick={() => setConfirmRetry(true)}>
                        {retrying ? 'Retrying…' : `Retry ${plural(stats.unsent, 'unsent share')}`}
                    </button>
                </SectionCard>
            )}

            <div>
                <div className="dl-head">
                    <h2>Recent shares</h2>
                    <button type="button" className="tb-btn ghost" onClick={() => navigate(`/activations/${id}/shares`)}>
                        All shares →
                    </button>
                </div>
                <TCard>
                    {failed ? (
                        <TEmpty message="Couldn’t load shares." actionLabel="Retry" onAction={load} />
                    ) : shares === null ? (
                        <TSkeleton rows={3} />
                    ) : stats.recent.length === 0 ? (
                        <TEmpty message="No shares yet — they appear here as guests share." />
                    ) : (
                        stats.recent.map((s) => {
                            const kind = s.type === 'text' ? 'SMS' : `${s.type ?? ''}`.toUpperCase();
                            const when = createdOf(s) ? moment(createdOf(s)).fromNow() : '';
                            return (
                                <div className="trow ev-row" key={s.id}>
                                    <span className="main">
                                        <span className="nm">{s.destination || '—'}</span>
                                        <span className="sub">{[kind, when].filter(Boolean).join(' · ')}</span>
                                    </span>
                                    <span className={s.sent ? 'ev-st ok' : 'ev-st'}>{s.sent ? 'Sent' : 'Unsent'}</span>
                                </div>
                            );
                        })
                    )}
                </TCard>
            </div>

            <ConfirmDialog
                open={confirmRetry}
                title="Retry unsent shares?"
                body={`This re-sends ${plural(stats.unsent, 'unsent share')} by email or text to the guests who didn’t receive them. Messages go out immediately.`}
                actionLabel={`Re-send ${stats.unsent.toLocaleString()}`}
                danger={false}
                onConfirm={retry}
                onClose={() => setConfirmRetry(false)}
            />
        </div>
    );
}

/* ---------------- reengagement ---------------- */

type Channels = 'both' | 'text' | 'email';
type Step = 'compose' | 'confirm' | 'progress';

const CHANNEL_LABEL: Record<Channels, string> = {
    both: 'Text + email',
    text: 'Text only',
    email: 'Email only',
};

const isRunning = (s: ReengagementSendObject | null | undefined) =>
    s?.status === 'pending' || s?.status === 'running';

function SendProgress({ send }: { send: ReengagementSendObject }) {
    const done = send.sentCount + send.failedCount + send.skippedCount;
    const pct = send.totalCount ? Math.min(100, Math.round((done / send.totalCount) * 100)) : 0;
    const running = isRunning(send);
    return (
        <div>
            <p className="dl-msg" role="status" aria-live="polite">
                {running && 'Sending… this keeps running on the server if you close or leave the page.'}
                {send.status === 'complete' && 'Done — every message was processed.'}
                {send.status === 'failed' && 'Finished with failures.'}
            </p>
            <div
                className="progress dl-prog"
                role="progressbar"
                aria-label="Reengagement progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
            >
                <div className="fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="dl-prog-l">
                {send.sentCount.toLocaleString()} sent
                {send.skippedCount > 0 && ` · ${send.skippedCount.toLocaleString()} skipped (opted out)`}
                {send.failedCount > 0 && <span className="bad"> · {send.failedCount.toLocaleString()} failed</span>}
                {` · ${send.totalCount.toLocaleString()} total`}
            </p>
        </div>
    );
}

function ReengageBlock() {
    const { activationId: id } = useEventContext();
    const { preview, isLoadingPreview, loadPreview, sends, loadSends, activeSend, setActiveSend, isStarting, start, error } =
        useReengagement(id);

    const [sendsLoaded, setSendsLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<Step>('compose');
    const [channels, setChannels] = useState<Channels>('both');
    const [textBody, setTextBody] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const startLock = useRef(false);
    const resumed = useRef(false);

    useEffect(() => {
        if (!id) return;
        loadPreview();
        loadSends().finally(() => setSendsLoaded(true));
    }, [id, loadPreview, loadSends]);

    /* a send started elsewhere and still running: show (and poll) it */
    useEffect(() => {
        if (resumed.current || activeSend || sends.length === 0) return;
        resumed.current = true;
        const live = sends.find(isRunning);
        if (live) setActiveSend(live);
    }, [sends, activeSend, setActiveSend]);

    const wantsText = channels !== 'email';
    const wantsEmail = channels !== 'text';
    const recipients = !preview
        ? 0
        : channels === 'text'
          ? preview.textCount
          : channels === 'email'
            ? preview.emailCount
            : preview.total;
    const busy = isRunning(activeSend);
    const canReview =
        !!preview &&
        recipients > 0 &&
        (!wantsText || textBody.trim().length > 0) &&
        (!wantsEmail || emailBody.trim().length > 0);
    const errorText = error ? (typeof error === 'string' ? error : 'Could not start the reengagement send') : null;

    const openComposer = () => {
        setStep('compose');
        setOpen(true);
        loadPreview();
    };

    const send = async () => {
        if (startLock.current || isStarting || !canReview || busy) return;
        startLock.current = true;
        try {
            const started = await start({
                channels,
                textBody: wantsText ? textBody : undefined,
                emailSubject: wantsEmail ? emailSubject || undefined : undefined,
                emailBody: wantsEmail ? emailBody : undefined,
            });
            if (started) {
                setStep('progress');
                setTextBody('');
                setEmailSubject('');
                setEmailBody('');
                loadSends();
            } else {
                setStep('compose');
            }
        } finally {
            startLock.current = false;
        }
    };

    const close = () => {
        if (isStarting) return;
        setOpen(false);
    };

    const edited = (fn: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => fn(e.target.value);

    const footer =
        step === 'progress' ? (
            <button type="button" className="tb-new" onClick={close}>
                {busy ? 'Close (keeps sending)' : 'Close'}
            </button>
        ) : step === 'confirm' ? (
            <>
                <button type="button" className="tb-btn" disabled={isStarting} onClick={() => setStep('compose')}>
                    Back
                </button>
                <button type="button" className="tb-new danger" disabled={isStarting || !canReview || busy} onClick={send}>
                    {isStarting ? 'Starting…' : `Send to ${plural(recipients, 'guest')}`}
                </button>
            </>
        ) : (
            <>
                <button type="button" className="tb-btn" onClick={close}>
                    Cancel
                </button>
                <button type="submit" className="tb-new" disabled={!canReview || busy}>
                    Review send
                </button>
            </>
        );

    return (
        <SectionCard title="Reengagement" note={busy ? 'sending' : undefined}>
            <p className="dl-p">
                Send everyone this event shared to a fresh link to their own gallery, with your message. Each unique
                email or phone number gets one message; opted-out guests are skipped.
            </p>
            <div className="dl-counts" aria-live="polite">
                {isLoadingPreview && !preview ? (
                    <span>Counting recipients…</span>
                ) : preview ? (
                    <>
                        <span>
                            <b>{preview.total.toLocaleString()}</b> unique recipients
                        </span>
                        <span>
                            <b>{preview.textCount.toLocaleString()}</b> SMS
                        </span>
                        <span>
                            <b>{preview.emailCount.toLocaleString()}</b> email
                        </span>
                        {preview.optedOutCount > 0 && (
                            <span>
                                <b>{preview.optedOutCount.toLocaleString()}</b> opted out (skipped)
                            </span>
                        )}
                    </>
                ) : (
                    <span>Couldn’t load recipients.</span>
                )}
            </div>

            {activeSend && !open && <SendProgress send={activeSend} />}

            <div className="dl-row ev-gap">
                <button
                    type="button"
                    className="tb-btn"
                    disabled={busy || !preview || preview.total === 0}
                    onClick={openComposer}
                >
                    {busy ? 'A send is in progress' : 'Compose reengagement…'}
                </button>
                {!preview && !isLoadingPreview && (
                    <button type="button" className="tb-btn ghost" onClick={() => loadPreview()}>
                        Retry
                    </button>
                )}
            </div>

            <div className="ev-gap">
                <div className="dl-head">
                    <h2>Past sends</h2>
                </div>
                {!sendsLoaded ? (
                    <TSkeleton rows={2} />
                ) : sends.length === 0 ? (
                    <p className="dl-msg">No reengagement sends yet.</p>
                ) : (
                    sends.slice(0, 10).map((s) => (
                        <div className="trow ev-row" key={s.id}>
                            <span className="main">
                                <span className="nm">{CHANNEL_LABEL[s.channels] ?? s.channels}</span>
                                <span className="sub">
                                    {[
                                        moment(s.createdAt).format('MMM D, h:mm a'),
                                        `${s.sentCount.toLocaleString()} of ${s.totalCount.toLocaleString()} sent`,
                                        s.failedCount > 0 ? `${s.failedCount.toLocaleString()} failed` : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </span>
                            </span>
                            <span
                                className={
                                    s.status === 'complete' ? 'ev-st ok' : s.status === 'failed' ? 'ev-st bad' : 'ev-st'
                                }
                            >
                                {s.status === 'complete'
                                    ? 'Done'
                                    : s.status === 'failed'
                                      ? 'Failed'
                                      : s.status === 'running'
                                        ? 'Sending'
                                        : 'Queued'}
                            </span>
                        </div>
                    ))
                )}
            </div>

            <Dialog
                open={open}
                title={step === 'confirm' ? 'Confirm reengagement' : step === 'progress' ? 'Reengagement' : 'Reengage guests'}
                onClose={close}
                onSubmit={step === 'compose' ? () => canReview && setStep('confirm') : undefined}
                footer={footer}
            >
                {step === 'progress' && activeSend && <SendProgress send={activeSend} />}

                {step === 'confirm' && (
                    <>
                        <p style={{ marginTop: 0 }}>
                            Send this message to <b>{plural(recipients, 'guest')}</b> now?
                        </p>
                        <div className="dl-counts">
                            {wantsText && (
                                <span>
                                    <b>{(preview?.textCount ?? 0).toLocaleString()}</b> by SMS
                                </span>
                            )}
                            {wantsEmail && (
                                <span>
                                    <b>{(preview?.emailCount ?? 0).toLocaleString()}</b> by email
                                </span>
                            )}
                            {(preview?.optedOutCount ?? 0) > 0 && (
                                <span>
                                    <b>{preview?.optedOutCount.toLocaleString()}</b> opted out (skipped)
                                </span>
                            )}
                        </div>
                        <p className="dl-msg">
                            Messages go out to real guests and can’t be recalled. The send keeps running on the server
                            after you close this window.
                        </p>
                        {errorText && <p className="dl-msg bad">{errorText}</p>}
                    </>
                )}

                {step === 'compose' && (
                    <>
                        <div className="field wide">
                            <span className="field-l">Send via</span>
                            <Segmented<Channels>
                                label="Send via"
                                value={channels}
                                onChange={setChannels}
                                options={[
                                    { value: 'both', label: 'Text + email' },
                                    { value: 'text', label: 'Text' },
                                    { value: 'email', label: 'Email' },
                                ]}
                            />
                        </div>
                        <div className="dl-counts ev-gap">
                            {preview ? (
                                <span>
                                    <b>{recipients.toLocaleString()}</b> recipient{recipients === 1 ? '' : 's'} on this
                                    channel
                                </span>
                            ) : (
                                <span>{isLoadingPreview ? 'Counting recipients…' : 'Couldn’t load recipients.'}</span>
                            )}
                        </div>
                        {wantsText && (
                            <Field label="Text message" help="Each guest’s gallery link is added automatically.">
                                <Textarea
                                    rows={3}
                                    value={textBody}
                                    placeholder="Relive the party! Your photos are waiting:"
                                    onChange={edited(setTextBody)}
                                />
                            </Field>
                        )}
                        {wantsEmail && (
                            <>
                                <Field label="Email subject" help="Optional — falls back to the gallery’s subject.">
                                    <TextInput
                                        value={emailSubject}
                                        placeholder="Your photos are waiting!"
                                        onChange={edited(setEmailSubject)}
                                    />
                                </Field>
                                <Field
                                    label="Email body"
                                    help="Plain text or HTML. *|SHAREURL|* places the gallery link; without it the link is added at the end."
                                >
                                    <Textarea
                                        rows={6}
                                        className="input textarea dl-code"
                                        value={emailBody}
                                        onChange={edited(setEmailBody)}
                                    />
                                </Field>
                            </>
                        )}
                        {errorText && <p className="dl-msg bad">{errorText}</p>}
                    </>
                )}
            </Dialog>
        </SectionCard>
    );
}

/* ---------------- webhook ---------------- */

const AUTH_LABELS: Record<AuthType, string> = {
    none: 'None',
    bearer: 'Bearer token',
    basic: 'Basic auth',
    custom_header: 'Custom header',
};

interface HookDraft {
    webhookUrl: string;
    enabled: boolean;
    authType: AuthType;
    authValue: string;
    customHeaderName: string;
}

function WebhookBlock({ toast }: { toast: ReturnType<typeof useToast> }) {
    const { activationId: id } = useEventContext();
    const { getWebhookSettings, createWebhookSettings, updateWebhookSettings, deleteWebhookSettings, testWebhook, error } =
        useWebhookSettings();

    const [settings, setSettings] = useState<WebhookSettings | null>(null);
    const [phase, setPhase] = useState<'loading' | 'ready' | 'denied'>('loading');
    const [touched, setTouched] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<HookDraft>({
        webhookUrl: '',
        enabled: true,
        authType: 'none',
        authValue: '',
        customHeaderName: '',
    });
    const [formError, setFormError] = useState<string | null>(null);
    const [busy, setBusy] = useState<null | 'save' | 'test' | 'delete'>(null);
    const [confirm, setConfirm] = useState<null | 'test' | 'delete'>(null);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const lock = useRef(false);

    const load = useCallback(() => {
        let alive = true;
        setPhase('loading');
        setTouched(false);
        getWebhookSettings(id)
            .then((data) => {
                if (!alive) return;
                setSettings(data);
                setPhase('ready');
            })
            .catch(() => alive && setPhase('denied'));
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!id) return;
        return load();
    }, [id, load]);

    /* the hook returns null for both "none" and a failed load; its error tells them apart */
    const loadFailed = phase === 'ready' && !settings && !touched && !!error;

    const run = async <T,>(kind: 'save' | 'test' | 'delete', fn: () => Promise<T>): Promise<T | undefined> => {
        if (lock.current) return undefined;
        lock.current = true;
        setBusy(kind);
        setTouched(true);
        try {
            return await fn();
        } finally {
            lock.current = false;
            setBusy(null);
        }
    };

    const openEdit = () => {
        setDraft({
            webhookUrl: settings?.webhookUrl ?? '',
            enabled: settings?.enabled ?? true,
            authType: settings?.authType ?? 'none',
            authValue: '',
            customHeaderName: settings?.customHeaderName ?? '',
        });
        setFormError(null);
        setEditing(true);
    };

    const save = async () => {
        if (!/^https?:\/\/.+/.test(draft.webhookUrl.trim())) {
            setFormError('Enter a full URL starting with https://');
            return;
        }
        if (draft.authType === 'custom_header' && !draft.customHeaderName.trim()) {
            setFormError('Custom header auth needs a header name.');
            return;
        }
        setFormError(null);
        const body: any = {
            webhookUrl: draft.webhookUrl.trim(),
            enabled: draft.enabled,
            authType: draft.authType,
        };
        if (draft.authValue) body.authValue = draft.authValue;
        if (draft.authType === 'custom_header') body.customHeaderName = draft.customHeaderName.trim();
        const result = await run('save', () =>
            (settings ? updateWebhookSettings(id, body) : createWebhookSettings(id, body)).catch(() => null),
        );
        if (result === undefined) return;
        if (result) {
            setSettings(result);
            setEditing(false);
            setTestResult(null);
            setDraft((d) => ({ ...d, authValue: '' }));
            toast.show('Webhook saved.');
        } else {
            setFormError('Couldn’t save the webhook — try again.');
        }
    };

    const sendTest = async () => {
        setTestResult(null);
        const result = await run('test', () => testWebhook(id));
        if (result) setTestResult(result);
    };

    const remove = async () => {
        const ok = await run('delete', () => deleteWebhookSettings(id).catch(() => false));
        if (ok === undefined) return;
        if (ok) {
            setSettings(null);
            setTestResult(null);
            toast.show('Webhook deleted.');
        } else {
            toast.show('Couldn’t delete the webhook — try again.');
        }
    };

    const valueLabel =
        draft.authType === 'bearer'
            ? 'Bearer token'
            : draft.authType === 'basic'
              ? 'Credentials (user:pass)'
              : 'Header value';

    return (
        <SectionCard title="Webhook" note="one per event">
            <p className="dl-p">
                Every share POSTs to your endpoint — destination, share identifiers, activation and gallery ids, share
                type and timestamp.
            </p>

            {phase === 'loading' ? (
                <TSkeleton rows={1} />
            ) : phase === 'denied' ? (
                <p className="dl-msg">Only admins can manage webhooks.</p>
            ) : loadFailed ? (
                <div className="dl-row">
                    <span className="dl-msg bad">Couldn’t load the webhook.</span>
                    <button type="button" className="tb-btn" onClick={load}>
                        Retry
                    </button>
                </div>
            ) : !settings ? (
                <button type="button" className="tb-btn" onClick={openEdit}>
                    Add webhook
                </button>
            ) : (
                <>
                    <div className="dl-hook">
                        <span className={settings.enabled ? 'dl-dot on' : 'dl-dot'} aria-hidden="true" />
                        <span className="dl-url" title={settings.webhookUrl}>
                            {settings.webhookUrl}
                        </span>
                        <span className="dl-tag">{settings.enabled ? 'On' : 'Off'}</span>
                        <span className="dl-tag">{AUTH_LABELS[settings.authType] ?? settings.authType}</span>
                    </div>
                    <div className="dl-row">
                        <button type="button" className="tb-btn" disabled={busy !== null} onClick={() => setConfirm('test')}>
                            {busy === 'test' ? 'Sending test…' : 'Send test'}
                        </button>
                        <button type="button" className="tb-btn" disabled={busy !== null} onClick={openEdit}>
                            Edit
                        </button>
                        <span className="sp" />
                        <button
                            type="button"
                            className="tb-btn danger-soft"
                            disabled={busy !== null}
                            onClick={() => setConfirm('delete')}
                        >
                            {busy === 'delete' ? 'Deleting…' : 'Delete'}
                        </button>
                    </div>
                    {testResult && (
                        <p className={testResult.success ? 'dl-msg ok ev-gap' : 'dl-msg bad ev-gap'} role="status">
                            {testResult.success ? (testResult.message ? `Test delivered — ${testResult.message}` : 'Test delivered.') : `Test failed — ${testResult.message}`}
                        </p>
                    )}
                </>
            )}

            <Dialog
                open={editing}
                title={settings ? 'Edit webhook' : 'Add webhook'}
                onClose={() => busy !== 'save' && setEditing(false)}
                onSubmit={save}
                footer={
                    <>
                        <button type="button" className="tb-btn" disabled={busy === 'save'} onClick={() => setEditing(false)}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={busy === 'save' || !draft.webhookUrl.trim()}>
                            {busy === 'save' ? 'Saving…' : 'Save webhook'}
                        </button>
                    </>
                }
            >
                <Field label="Webhook URL">
                    <TextInput
                        type="url"
                        className="input dl-code"
                        placeholder="https://hooks.example.com/…"
                        value={draft.webhookUrl}
                        onChange={(e) => setDraft((d) => ({ ...d, webhookUrl: e.target.value }))}
                    />
                </Field>
                <Field label="Authentication">
                    <Select
                        value={draft.authType}
                        onChange={(e) => setDraft((d) => ({ ...d, authType: e.target.value as AuthType }))}
                    >
                        {(Object.entries(AUTH_LABELS) as [AuthType, string][]).map(([v, l]) => (
                            <option key={v} value={v}>
                                {l}
                            </option>
                        ))}
                    </Select>
                </Field>
                {draft.authType === 'custom_header' && (
                    <Field label="Header name">
                        <TextInput
                            className="input dl-code"
                            placeholder="X-API-Key"
                            value={draft.customHeaderName}
                            onChange={(e) => setDraft((d) => ({ ...d, customHeaderName: e.target.value }))}
                        />
                    </Field>
                )}
                {draft.authType !== 'none' && (
                    <Field
                        label={valueLabel}
                        help={settings?.hasAuthConfigured ? 'Stored and hidden — leave empty to keep it.' : undefined}
                    >
                        <TextInput
                            type="password"
                            autoComplete="new-password"
                            className="input dl-code"
                            value={draft.authValue}
                            onChange={(e) => setDraft((d) => ({ ...d, authValue: e.target.value }))}
                        />
                    </Field>
                )}
                <div className="ev-gap">
                    <Toggle
                        label="Enabled"
                        checked={draft.enabled}
                        onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                    />
                </div>
                {formError && <p className="dl-msg bad ev-gap">{formError}</p>}
            </Dialog>

            <ConfirmDialog
                open={confirm === 'test'}
                title="Send a test webhook?"
                body={`This sends a real test request to ${settings?.webhookUrl ?? 'your endpoint'}.`}
                actionLabel="Send test"
                danger={false}
                onConfirm={sendTest}
                onClose={() => setConfirm(null)}
            />
            <ConfirmDialog
                open={confirm === 'delete'}
                title="Delete webhook?"
                body={`Shares stop notifying ${settings?.webhookUrl ?? 'the endpoint'} immediately. This can’t be undone.`}
                actionLabel="Delete"
                danger
                onConfirm={remove}
                onClose={() => setConfirm(null)}
            />
        </SectionCard>
    );
}

/* ---------------- page ---------------- */

function DeliveryBody() {
    const navigate = useNavigate();
    const { activationId: id } = useEventContext();
    const toast = useToast();
    return (
        <div className="ev-page">
            <Crumb label="Sharing & Delivery" onClick={() => navigate(`/activations/${id}/sharing`)} />
            <PageHeadActions title="Delivery" sub="Share outcomes, retries, reengagement and the share webhook" />
            <div className="stack dl-stack">
                <SharesBlock toast={toast} />
                <ReengageBlock />
                <WebhookBlock toast={toast} />
            </div>
            {toast.node}
        </div>
    );
}

export default function DeliveryV3() {
    return (
        <EventPage section="sharing">
            <DeliveryBody />
        </EventPage>
    );
}
